// Toolbar inside .main-wrapper — left = resolution-bar, then view-mode
// controls (Side by Side / Slider / Overlay / Diff / History / Last), zoom
// controls, annotation controls (Add Remark + toggle remarks visibility).
// Mirrors legacy stbarts toolbar (index.html L1711-1762).

import { For, Show } from 'solid-js';
import { actions, stores } from '../state/store';
import type { ViewMode } from '../state/store';
import { ResolutionsSelector } from './ResolutionsSelector';

const VIEW_MODES: Array<{ key: ViewMode; label: string }> = [
  { key: 'compare', label: 'Side by Side' },
  { key: 'slider', label: 'Slider' },
  { key: 'overlay', label: 'Overlay' },
  { key: 'diff', label: 'Diff' },
  { key: 'history', label: 'History' },
  { key: 'last', label: 'Last' },
];

const ZOOM_STEP = 10;

export function Toolbar() {
  function setMode(key: ViewMode) {
    actions.setViewMode(key);
  }
  function bumpZoom(delta: number) {
    const next = Math.max(10, Math.min(400, stores.state.zoomPercent + delta));
    actions.setZoom(next);
  }
  function resetZoom() {
    actions.setZoom(100);
  }
  function setZoomFitMode(mode: 'fitWidth' | 'fitHeight' | 'auto' | null) {
    actions.setZoomFitMode(mode);
  }

  return (
    <div class="toolbar">
      <ResolutionsSelector />

      <div class="toolbar-controls">
        <For each={VIEW_MODES}>
          {(mode) => (
            <button
              type="button"
              class={stores.state.viewMode === mode.key ? 'active' : ''}
              onClick={() => setMode(mode.key)}
            >
              {mode.label}
            </button>
          )}
        </For>
      </div>

      <div class="zoom-controls">
        <button type="button" title="Zoom out (-)" onClick={() => bumpZoom(-ZOOM_STEP)}>
          −
        </button>
        <span class="zoom-level">{stores.state.zoomPercent}%</span>
        <button type="button" title="Zoom in (+)" onClick={() => bumpZoom(ZOOM_STEP)}>
          +
        </button>
        <button type="button" title="Reset zoom (0)" onClick={resetZoom}>
          Reset
        </button>
        {/* Zoom-mode buttons get the `active` class only after a target is
            selected — legacy stbarts only calls updateZoomModeButtons inside
            renderTarget() so at boot no zoom button is highlighted, even
            though currentZoomMode defaults to 'auto'. */}
        <button
          type="button"
          title="Fit width (W)"
          class={stores.state.activeTargetId && stores.state.zoomFitMode === 'fitWidth' ? 'active' : ''}
          onClick={() => setZoomFitMode('fitWidth')}
        >
          ↔
        </button>
        <button
          type="button"
          title="Fit height (H)"
          class={stores.state.activeTargetId && stores.state.zoomFitMode === 'fitHeight' ? 'active' : ''}
          onClick={() => setZoomFitMode('fitHeight')}
        >
          ↕
        </button>
        <button
          type="button"
          title="Auto fit (F)"
          class={stores.state.activeTargetId && stores.state.zoomFitMode === 'auto' ? 'active' : ''}
          onClick={() => setZoomFitMode('auto')}
        >
          Auto
        </button>
      </div>

      <div class="annotation-controls">
        <button
          type="button"
          class={`annotate-btn ${stores.state.annotateMode ? 'active' : ''}`}
          title="Add remark (A)"
          onClick={() => actions.toggleAnnotateMode()}
        >
          + Remark
        </button>
        <button
          type="button"
          class={`toggle-remarks-btn ${stores.state.remarksVisible ? 'active' : ''}`}
          title="Show/hide remarks on screenshots (V)"
          onClick={() => actions.toggleRemarksVisible()}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
        </button>
      </div>

      <Show when={stores.state.activeProjectId}>
        <span
          class={`sync-indicator ${stores.state.viewMode === 'compare' ? '' : 'disabled'}`}
          title="Synchronized scrolling"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
          </svg>
          Sync
        </span>
      </Show>
    </div>
  );
}
